import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { errorMessage } from "@/lib/api";
import { useTenantConfig } from "@/hooks/useTenantConfig";
import PortalConfigFields from "@/components/PortalConfigFields";
import BrandingPreview from "@/components/BrandingPreview";
import { Button } from "@/components/ui/button";
import { defaultFeatureConfig } from "@/lib/tenant-theme";
import { Loader2 } from "lucide-react";

/**
 * Portals — step 3 of tenant setup. Per-portal name, tagline, subdomain,
 * nav modules and per-portal color overrides for the Talent and Hub
 * portals. Persists featureFlags (incl. portals.*) and reads branding for
 * the live preview, both through the real /configuration endpoint.
 */
export default function AdminPortals() {
  const { config, loading, save, error: cfgError, setError } = useTenantConfig();
  const [featureConfig, setFeatureConfig] = useState(defaultFeatureConfig());
  const [branding, setBranding] = useState({ colors: {} });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!config) return;
    setBranding(config.branding || { colors: {} });
    setFeatureConfig({ ...defaultFeatureConfig(), ...(config.featureFlags || {}) });
  }, [config]);

  const includedPortals = Object.keys(featureConfig.portals || { talent: 1, hub: 1 });

  const onSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await save({ featureFlags: featureConfig });
      setSaved(true);
    } catch (err) {
      const m = errorMessage(err);
      if (m) setError(m);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout variant="tenant">
      <h1 className="font-heading text-2xl font-bold">Portals</h1>
      <p className="mt-1 text-muted-foreground">Configure the Talent and Hub portal experience.</p>
      {cfgError && <p className="mt-6 text-sm text-destructive" data-testid="portals-error">{cfgError}</p>}
      {loading && <Loader2 className="mt-8 h-6 w-6 animate-spin text-primary" />}

      {!loading && (
        <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_380px]">
          <div>
            <PortalConfigFields featureConfig={featureConfig} onChange={setFeatureConfig} portals={includedPortals} branding={branding} />
            <div className="mt-6 flex items-center gap-3">
              <Button onClick={onSave} disabled={saving} data-testid="portals-save-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
              </Button>
              {saved && <p className="text-sm text-emerald-600" data-testid="portals-saved">Saved — applied across Talent &amp; Hub.</p>}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Live preview</p>
            <BrandingPreview branding={branding} portals={includedPortals} featureConfig={featureConfig} />
          </div>
        </div>
      )}
    </Layout>
  );
}
